import React from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Agendar: React.FC = () => {
  const { elementRef, isVisible } = useScrollAnimation({ threshold: 0.2, triggerOnce: false });

  const clinics = [
    {
      name: "Parahyba Mall (Bessa)",
      address: "R. Bacharel José de Oliveira Curchatuz, 850",
      city: "Jardim Oceania - João Pessoa, PB",
      phone: "(83) 99861-5180",
      whatsappLink: "https://wa.me/5583998615180?text=Olá! Gostaria de agendar uma avaliação na unidade Parahyba Mall.",
      image: "/assets/img/fachadas/02.webp",
      features: ["Estrutura Moderna", "Estacionamento", "Fácil Acesso"]
    },
    {
      name: "Shopping Tambiá (Centro)",
      address: "R. Dep. Odon Bezerra, 184",
      city: "Tambiá - João Pessoa, PB",
      phone: "(83) 99311-0469",
      whatsappLink: "https://wa.me/5583993110469?text=Olá! Gostaria de agendar uma avaliação na unidade Shopping Tambiá.",
      image: "/assets/img/fachadas/tambia.jpg",
      features: ["Centro da Cidade", "Tradição", "Atendimento Personalizado"]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E8E4DF] via-[#EBE7E2] to-[#E0DCD7]">
      {/* Hero Section */}
      <section className="relative pt-28 pb-20 lg:pt-32 lg:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-tr from-white/40 via-transparent to-primary/5"></div>
        <div className="absolute top-20 right-[15%] w-32 h-32 bg-gradient-to-br from-secondary/10 to-transparent rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-32 left-[10%] w-40 h-40 bg-gradient-to-tr from-primary/10 to-transparent rounded-full blur-3xl animate-float-delay"></div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-6 animate-fade-in">
              <span className="bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 bg-clip-text text-transparent">
                Agende Sua
              </span>
              <br />
              <span className="bg-gradient-to-r from-secondary via-primary to-secondary bg-clip-text text-transparent">
                Avaliação
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-4 animate-fade-in-delay">
              Escolha a unidade mais próxima e agende sua consulta pelo WhatsApp.
            </p>
            <p className="text-base text-gray-600 animate-fade-in-delay-2">
              Nossa equipe está pronta para atendê-lo com excelência.
            </p>
          </div>
        </div>
      </section>

      {/* Clinics Section */}
      <section ref={elementRef as React.RefObject<HTMLElement>} className="py-16 pb-32">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto">
            {clinics.map((clinic, index) => (
              <div
                key={index}
                className={`group relative bg-white rounded-3xl overflow-hidden shadow-xl transition-all duration-700 hover:shadow-2xl hover:-translate-y-2 ${
                  isVisible ? 'animate-fade-in opacity-100' : 'opacity-0 translate-y-8'
                }`}
                style={{ animationDelay: `${index * 200}ms` }}
              >
                {/* Image */}
                <div className="relative h-64 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent z-10"></div>
                  <img
                    src={clinic.image}
                    alt={clinic.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute top-6 right-6 z-20">
                    <div className="bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg">
                      <span className="text-sm font-bold text-secondary">Unidade {clinic.name}</span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8">
                  <h2 className="text-2xl md:text-3xl font-serif font-bold text-gray-800 mb-3">
                    {clinic.name}
                  </h2>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex items-start gap-3 text-gray-600">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <div>
                        <p className="font-medium">{clinic.address}</p>
                        <p className="text-sm">{clinic.city}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 text-gray-600">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-secondary flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <p className="font-medium">{clinic.phone}</p>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {clinic.features.map((feature, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 text-gray-700 text-sm rounded-full border border-gray-200"
                      >
                        <svg className="w-3.5 h-3.5 text-secondary" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </span>
                    ))}
                  </div>

                  {/* WhatsApp Button */}
                  <a
                    href={clinic.whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group/btn relative w-full inline-flex items-center justify-center gap-3 px-6 py-4 bg-[#25D366] text-white font-semibold text-base overflow-hidden transition-all duration-300 hover:shadow-2xl hover:scale-105 rounded-xl"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-[#128C7E] to-[#075E54] transform -translate-x-full group-hover/btn:translate-x-0 transition-transform duration-500"></span>
                    <svg className="w-6 h-6 relative z-10" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    <span className="relative z-10">Agendar via WhatsApp</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 relative z-10 group-hover/btn:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Agendar;
